import { ref, Ref } from 'vue';
import { IncentiveStep } from '../../../../../interfaces/enums/incentive-step';
import { IIncentiveV2Store } from "@/domain/dto/store.incentive-v2.interfaces";
import { useOwnModal } from '../../Composables/useOwnModal';

export default class Optinv2 {
	public root: Ref<HTMLElement | null> = ref(null);
	private intersectionObserver: IntersectionObserver | null = null;
	private wasVisible = false;
	private hasMounted = false;
	private readonly modal = useOwnModal(() => !this.store.getters.isModal(),
		{
			cssClassName: 'IncentiveModal',
			destroyOnClose: true,
			closeOnOverlayClick: true,
			onClose: () => {
				if (this.store.getters.step() === IncentiveStep.OPT_IN) {
					void this.store.actions.dismissOptIn();
				}
			},
		},
	);
	public modalContainer = this.modal.modalContainer;

	constructor(public store: IIncentiveV2Store) {}

	public async accept(): Promise<void> {
		await this.store.actions.acceptOptIn();
		// eslint-disable-next-line @typescript-eslint/no-unsafe-call
		window._cio?.track('scholarships_update_accept', {
			identifier: this.store.state.uniqueIdentifier,
			incentiveType: this.store.state.incentiveType,
			location: this.store.state.location
		});
	}

	public async deny(): Promise<void> {
		await this.store.actions.denyOptIn();
	}

	public mounted(): void {
		if (this.hasMounted) return;
		this.hasMounted = true;

		this.modal.open();

		if (!this.root.value || this.intersectionObserver) return;

		this.intersectionObserver = new IntersectionObserver(
			(entries) => this.handleVisibilityChange(entries),
			{ threshold: 0 },
		);
		this.intersectionObserver.observe(this.root.value);
	}

	public unmount(): void {
		this.intersectionObserver?.disconnect();
		this.intersectionObserver = null;
		this.modal.close();
	}

	/* catches a host-owned modal (or any other container) hiding/removing us without going through
		accept/deny/onClose - the only signal we can rely on since we have no hook into it. Guarded by
		the step check so it never double-fires alongside main.ts's onClose for self-owned modals. */
	private handleVisibilityChange(entries: IntersectionObserverEntry[]): void {
		const entry = entries[0];
		if (!entry) return;

		if (entry.isIntersecting) {
			this.wasVisible = true;
			return;
		}

		if (!this.wasVisible) return;

		this.unmount();

		if (this.store.getters.step() === IncentiveStep.OPT_IN) {
			void this.store.actions.dismissOptIn();
		}
	}
}
